/* eslint-disable @repo/no-style-props, @repo/no-abstracted-overlay-trigger, @repo/component-file-structure */
"use client";

import * as React from "react";
import { Check } from "@langfuse/icons";
import { DropdownIndicator } from "@/src/components/design-system/DropdownIndicator/DropdownIndicator";

import { cn } from "@/src/utils/tailwind";
import { Button } from "@/src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { Badge } from "@/src/components/design-system/Badge/Badge";

interface ComboboxOption<T extends string | number | boolean | { id: string }> {
  value: T;
  label?: string;
  /** Rendered before the label (e.g. a brand/ownership icon). */
  icon?: React.ReactNode;
  /** Small tag rendered after the label (e.g. "Default"). */
  badge?: string;
  disabled?: boolean;
}

export interface ComboboxOptionGroup<
  T extends string | number | boolean | { id: string },
> {
  heading?: string;
  options: ComboboxOption<T>[];
}

export type ComboboxOptionsInput<
  T extends string | number | boolean | { id: string },
> = ComboboxOption<T>[] | ComboboxOptionGroup<T>[];

export interface ComboboxProps<
  T extends string | number | boolean | { id: string },
> {
  options: ComboboxOptionsInput<T>;
  value?: T;
  onValueChange?: (value: T) => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  footer?: (args: { search: string; close: () => void }) => React.ReactNode;
}

function isGroupedOptions<T extends string | number | boolean | { id: string }>(
  options: ComboboxOptionsInput<T>,
): options is ComboboxOptionGroup<T>[] {
  return (
    options.length > 0 &&
    typeof options[0] === "object" &&
    "options" in options[0] &&
    Array.isArray(options[0].options)
  );
}

function isEqual<T extends string | number | boolean | { id: string }>(
  a: T | undefined,
  b: T | undefined,
): boolean {
  if (
    a &&
    b &&
    typeof a === "object" &&
    typeof b === "object" &&
    "id" in a &&
    "id" in b
  ) {
    return (a as { id: string }).id === (b as { id: string }).id;
  }
  return a === b;
}

export function Combobox<T extends string | number | boolean | { id: string }>({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  emptyText = "No option found.",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  name,
  footer,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const close = () => {
    setOpen(false);
    setSearch("");
  };
  const footerContent = footer?.({ search, close });

  const selectedOption = React.useMemo(() => {
    if (isGroupedOptions(options)) {
      for (const group of options) {
        const found = group.options.find((opt) => isEqual(opt.value, value));
        if (found) return found;
      }
      return undefined;
    }
    return options.find((option) => isEqual(option.value, value));
  }, [options, value]);

  const buttonText = selectedOption
    ? (selectedOption.label ?? String(selectedOption.value))
    : placeholder;

  return (
    <Popover
      open={!disabled && open}
      onOpenChange={(nextOpen) => {
        if (disabled) return;
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={!disabled && open}
          className={cn(
            "w-full justify-between text-xs font-normal",
            !value && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
          name={name}
        >
          {selectedOption?.icon && (
            <span className="mr-1.5 flex shrink-0 items-center">
              {selectedOption.icon}
            </span>
          )}
          <span className="truncate" title={buttonText}>
            {buttonText}
          </span>
          {selectedOption?.badge && (
            <span className="ml-1.5 flex shrink-0">
              <Badge
                color="filled"
                font="mono"
                size="sm"
                text={selectedOption.badge}
              />
            </span>
          )}
          <span className="ml-2 flex">
            <DropdownIndicator direction="up-down" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        onCloseAutoFocus={(event) => {
          if (disabled) event.preventDefault();
        }}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-xs"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {isGroupedOptions(options) ? (
              // Render with groups
              options.map((group, groupIndex) => (
                <CommandGroup key={groupIndex} heading={group.heading}>
                  {group.options.map((option) => (
                    <CommandItem
                      key={
                        typeof option.value === "object"
                          ? (option.value as { id: string }).id
                          : String(option.value)
                      }
                      value={
                        typeof option.value === "object"
                          ? (option.value as { id: string }).id
                          : String(option.value)
                      }
                      keywords={[option.label ?? String(option.value)]}
                      disabled={option.disabled}
                      onSelect={() => {
                        if (!option.disabled && onValueChange) {
                          onValueChange(option.value as T);
                          close();
                        }
                      }}
                      className={cn(
                        "text-xs",
                        option.disabled && "text-muted-foreground line-through",
                      )}
                    >
                      <Check
                        size="base"
                        __rawClassName={cn(
                          "mr-2",
                          isEqual(value as T | undefined, option.value)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      {option.icon && (
                        <span className="mr-1.5 flex shrink-0 items-center">
                          {option.icon}
                        </span>
                      )}
                      {option.label ?? String(option.value)}
                      {option.badge && (
                        <span className="ml-auto flex shrink-0">
                          <Badge
                            color="filled"
                            font="mono"
                            size="sm"
                            text={option.badge}
                          />
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            ) : (
              // Flat rendering (backward compatible)
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={
                      typeof option.value === "object"
                        ? (option.value as { id: string }).id
                        : String(option.value)
                    }
                    value={
                      typeof option.value === "object"
                        ? (option.value as { id: string }).id
                        : String(option.value)
                    }
                    keywords={[option.label ?? String(option.value)]}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (!option.disabled && onValueChange) {
                        onValueChange(option.value as T);
                        close();
                      }
                    }}
                    className={cn(
                      "text-xs",
                      option.disabled && "text-muted-foreground line-through",
                    )}
                  >
                    <Check
                      size="base"
                      __rawClassName={cn(
                        "mr-2",
                        isEqual(value as T | undefined, option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.icon && (
                      <span className="mr-1.5 flex shrink-0 items-center">
                        {option.icon}
                      </span>
                    )}
                    {option.label ?? String(option.value)}
                    {option.badge && (
                      <span className="ml-auto flex shrink-0">
                        <Badge
                          color="filled"
                          font="mono"
                          size="sm"
                          text={option.badge}
                        />
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        {footerContent ? (
          <div className="border-border border-t p-1">{footerContent}</div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
